import type { FC } from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { formatNumber } from '@wix/da-shared-react/pkg/utils/formatting';
import SelectableListItem from '../SelectableListItem';
import BaseListCell from '../BaseListItem/BaseListCell';
import DeviationThumb from '../../_partials/DeviationThumb';
import DeviationTitle from '../../_partials/DeviationTitle';
import DeviationInfo from '../../_partials/DeviationInfo';
import DeviationActions from '../../../tableActions/DeviationActions';
import ExclusiveOwner from './_partials/ExclusiveOwner';
import ExclusivePriceStat from './_partials/ExclusivePriceStat';
import ExclusiveOffersStat from './_partials/ExclusiveOffersStat';
import StatItem from '../../_partials/StatItem';
import { StudioDeviationListContext } from '../../context';

import s from './ExclusiveListItem.scss';

interface Props {
  deviation: PapiDeviation;
  offset: number;
  selected: number[];
  onSelect: (index: number, isSelected: boolean, isShiftKey?: boolean) => void;
}

const ExclusiveListItem: FC<Props> = ({ deviation, offset, selected, onSelect }) => {
  const { t } = useTranslation();
  const { actionsColRef, actionsColWidth } = useContext(StudioDeviationListContext);
  const viewsCount = deviation.stats?.views ?? 0;

  // We use the first row as a probe to determine the size of columns
  const shouldAddColumnRefs = offset === 0;

  return (
    <SelectableListItem offset={offset} selected={selected} onSelect={onSelect}>
      <BaseListCell>
        <DeviationThumb deviation={deviation} />
      </BaseListCell>
      <BaseListCell className={s['cell-title']}>
        <DeviationTitle deviation={deviation} showSaleIndicators={false} />
        <DeviationInfo deviation={deviation} />
        <ExclusiveOwner deviation={deviation} />
      </BaseListCell>
      <BaseListCell>
        <ExclusivePriceStat deviation={deviation} />
      </BaseListCell>
      <BaseListCell>
        <ExclusiveOffersStat deviation={deviation} />
      </BaseListCell>
      <BaseListCell>
        <StatItem
          title={t('studio.products.exclusive.stats.views')}
          value={formatNumber(viewsCount)}
        />
      </BaseListCell>
      <BaseListCell ref={shouldAddColumnRefs ? actionsColRef : undefined} overflowHidden>
        <DeviationActions deviation={deviation} offset={offset} columnWidth={actionsColWidth} />
      </BaseListCell>
    </SelectableListItem>
  );
};
ExclusiveListItem.displayName = 'ExclusiveListItem';

export default ExclusiveListItem;
